/*
  ════════════════════════════════════════════════════════════════════════════
  MONEY

  Amounts are integers of pesewas everywhere — in the database, over the wire,
  and in every piece of state in this app. This file is the only boundary where
  they become a decimal string, and the only place one is read back.

  WHY PARSING IS DONE ON THE STRING, NOT WITH parseFloat

  The obvious implementation is `Math.round(parseFloat(input) * 100)`. It is
  wrong often enough to matter: `parseFloat('8.235') * 100` is 823.4999999999999
  and `parseFloat('1.005') * 100` is 100.49999999999999, so both round DOWN a
  pesewa. On a term's fees across a school that is a reconciliation that never
  quite balances and nobody can explain.

  Splitting the string on the decimal point and assembling an integer from the
  two halves has no floating-point step at all, so it cannot drift.
  ════════════════════════════════════════════════════════════════════════════
*/

/** `160000` → `"1,600.00"`. The currency is NOT included. */
export function formatMinor(minor: number): string {
  const negative = minor < 0;
  const abs = Math.abs(Math.trunc(minor));
  const major = Math.trunc(abs / 100);
  const pesewas = abs % 100;
  return `${negative ? '-' : ''}${major.toLocaleString('en-GH')}.${String(pesewas).padStart(2, '0')}`;
}

/** `160000` → `"GHS 1,600.00"`. */
export function formatMoney(minor: number, currency = 'GHS'): string {
  return `${currency} ${formatMinor(minor)}`;
}

/**
 * `"1,600.50"` → `160050`. Returns null when the input is not a usable amount,
 * so a caller can tell "nothing typed yet" from "typed something invalid".
 */
export function parseMajorToMinor(input: string): number | null {
  const cleaned = input.replace(/[,\s]/g, '').replace(/^GHS/i, '');
  if (cleaned === '') return null;

  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;

  const whole = Number(match[1]);
  /* '.5' means fifty pesewas, not five — pad before reading it. */
  const frac = Number((match[2] ?? '0').padEnd(2, '0'));
  if (!Number.isSafeInteger(whole)) return null;

  return whole * 100 + frac;
}

const TERM_LABELS: Record<string, string> = {
  term_1: 'Term 1',
  term_2: 'Term 2',
  term_3: 'Term 3',
};

export function termLabel(term: string): string {
  return TERM_LABELS[term] ?? term;
}

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  momo: 'Mobile money',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  waiver: 'Waiver',
};

export function methodLabel(method: string): string {
  return METHOD_LABELS[method] ?? method;
}
